/**
 * Local-first persistence: IndexedDB, one record per sheet.
 *
 * The app opens instantly and works offline. Nothing here blocks first paint — the canvas
 * mounts, and the sheet arrives when it arrives. This module is also the seam a Supabase
 * adapter slots into in Phase 5, which is why the record shape already carries the fields that
 * table will need.
 */

import { deserialise, emptyScene, serialise, type Scene } from './scene';

export type SheetKind = 'scratch' | 'kept';

export interface SheetMeta {
  id: string;
  title: string;
  course: string;
  kind: SheetKind;
  createdAt: number;
  updatedAt: number;
}

export interface SheetRecord extends SheetMeta {
  scene: Scene;
}

/**
 * The original scratch sheet's id.
 *
 * It stays `scratch` forever. Scratch is a stack of sheets now rather than one — every press of
 * the Scratch door mints a new one — but this row has been on the owner's device since before
 * that was true, and it holds work. It is an address, not a name; changing it orphans that sheet
 * exactly the way renaming `DB_NAME` would orphan every sheet.
 */
export const SCRATCH_ID = 'scratch';

/**
 * A fresh scratch sheet's id.
 *
 * > *"when you open a scratch from the home menu, make it a new sheet, rather then reenter the
 * > old, but obviously the person themselves can go back to their old if they want to"*
 *
 * Time-ordered prefix so a list of them sorts by itself even before any of them has been written.
 */
export const newScratchId = (): string =>
  `scratch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const isScratchId = (id: string): boolean => id === SCRATCH_ID || id.startsWith('scratch-');

export const newSheet = (kind: SheetKind, title: string, id?: string): SheetRecord => {
  const now = Date.now();
  return {
    id: id ?? (kind === 'scratch' ? newScratchId() : `sheet-${now.toString(36)}-${Math.random().toString(36).slice(2, 7)}`),
    title,
    course: '',
    kind,
    createdAt: now,
    updatedAt: now,
    scene: emptyScene(),
  };
};

/**
 * The stored form of a whole sheet.
 *
 * Every field written here is read back by `fromStored`, and the pair is tested for deep
 * equality. Kairos shipped a bug where a field was read but never written, so the link it
 * carried vanished silently on reload; a mapping layer with no test is exactly where that
 * lives, and this is the test's target.
 */
export interface StoredSheet extends SheetMeta {
  scene: ReturnType<typeof serialise>;
}

export const toStored = (sheet: SheetRecord): StoredSheet => ({
  id: sheet.id,
  title: sheet.title,
  course: sheet.course,
  kind: sheet.kind,
  createdAt: sheet.createdAt,
  updatedAt: sheet.updatedAt,
  scene: serialise(sheet.scene),
});

export const fromStored = (stored: StoredSheet): SheetRecord => ({
  id: stored.id,
  title: stored.title,
  course: stored.course,
  kind: stored.kind,
  createdAt: stored.createdAt,
  updatedAt: stored.updatedAt,
  scene: deserialise(stored.scene),
});

// ── IndexedDB ──────────────────────────────────────────────────────────────────────────────

/*
 * `euclid`, and it stays `euclid` however the app is named.
 *
 * The product is called Paint 6000 now. This string is not a name, it is an ADDRESS: every sheet,
 * project and log entry already on a device is filed under it, and renaming it would open a fresh
 * empty database and orphan all of it with no error and no way back. Same for `euclid.*` in
 * localStorage, `euclid-teams`, `euclid-books`, `euclid-radio`, and the `euclid:` window events.
 */
const DB_NAME = 'euclid';

/*
 * Which database this device is reading and writing RIGHT NOW.
 *
 * It starts as `euclid` and stays there for a guest and for the account that claims this device's
 * existing work; another account gets `euclid-u-<id>`. `lib/workspaces.ts` owns the rules and is
 * the only thing that should ever call `useDatabase` — this module holds the seam, not the policy.
 *
 * The seam is here rather than in every caller because `openDb` is the single door: sheets, the
 * spine, projects, the log and both halves of the transfer file all come through it, so one
 * variable switches the whole workspace and nothing else in the app has to know it happened.
 */
let active = DB_NAME;

/** The database in use. `euclid` unless an account other than this device's owner is signed in. */
export const databaseName = (): string => active;

/**
 * Point the app at a different workspace, closing the one it was holding.
 *
 * Closing matters: an open handle blocks a version change on ANY connection to that name, and in
 * WebKit it blocks writes outright — which is the exact failure that made the built-in chapters
 * silently refuse to install. A switched-away database must not be left open.
 *
 * Callers reload whatever they had read out of the old one. This function moves the pointer; it
 * does not move data, and it never deletes any.
 */
export async function useDatabase(name: string): Promise<void> {
  if (name === active) return;
  const previous = handle;
  handle = null;
  active = name;
  try { (await previous)?.close(); } catch { /* a failed open has nothing to close */ }
}

/**
 * Version 3 adds `projects` and `meta`. Nothing else changed.
 *
 * The upgrade is additive on purpose: every store is created behind a `contains` guard, so a
 * database already at 2 gains one empty store and keeps every course, chapter, task, log entry
 * and sheet it held. There is no data rewrite anywhere in this handler, which is the only kind
 * of migration that cannot lose a record — and `tests/drive-projects.mjs` seeds a database at
 * version 2, opens the app, and counts the records back out to prove it rather than assert it.
 */
export const DB_VERSION = 3;
const STORE = 'sheets';

/** Every store in the database. Sheets hold working; the rest hold the spine. */
export const STORES = {
  sheets: 'sheets',
  courses: 'courses',
  chapters: 'chapters',
  tasks: 'tasks',
  log: 'log',
  projects: 'projects',
  /** One-off bookkeeping: which repairs have already run against this database. */
  meta: 'meta',
} as const;

let handle: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (handle) return handle;
  handle = new Promise((resolve, reject) => {
    const request = indexedDB.open(active, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(STORES.courses)) {
        db.createObjectStore(STORES.courses, { keyPath: 'id' }).createIndex('order', 'order');
      }
      if (!db.objectStoreNames.contains(STORES.chapters)) {
        const store = db.createObjectStore(STORES.chapters, { keyPath: 'id' });
        store.createIndex('courseId', 'courseId');
      }
      if (!db.objectStoreNames.contains(STORES.tasks)) {
        const store = db.createObjectStore(STORES.tasks, { keyPath: 'id' });
        store.createIndex('chapterId', 'chapterId');
        store.createIndex('courseId', 'courseId');
      }
      if (!db.objectStoreNames.contains(STORES.log)) {
        db.createObjectStore(STORES.log, { keyPath: 'id' }).createIndex('at', 'at');
      }
      if (!db.objectStoreNames.contains(STORES.projects)) {
        db.createObjectStore(STORES.projects, { keyPath: 'id' }).createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    /**
     * A second tab still holding the old version blocks the upgrade.
     *
     * Without this the promise never settles and the app hangs on a blank screen with nothing in
     * the console — the same shape as the test gotcha in CLAUDE.md, and far harder to diagnose in
     * a real window. Failing loudly is worse than nothing only if nothing is honest, and it isn't.
     */
    request.onblocked = () => reject(new Error('Paint 6000 is open in another tab — close it to finish updating.'));
  });
  return handle;
}

const run = async <T,>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = work(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Something was written. Said once, here, where every write already passes.
 *
 * `lib/cloudwork.ts` listens and copies the change up to the account after a quiet moment. A
 * window event rather than an import because storage is the bottom of the stack: the copier knows
 * about storage, and storage must not have to know about the copier.
 */
const wrote = (): void => {
  try { window.dispatchEvent(new Event('euclid:wrote')); } catch { /* not a window */ }
};

export async function saveSheet(sheet: SheetRecord): Promise<void> {
  await run('readwrite', store => store.put(toStored(sheet)) as IDBRequest<IDBValidKey>);
  wrote();
}

export async function loadSheet(id: string): Promise<SheetRecord | null> {
  const stored = await run<StoredSheet | undefined>('readonly', store => store.get(id));
  return stored ? fromStored(stored) : null;
}

/**
 * Metadata only, never the scenes.
 *
 * Reading every sheet's full scene to render a list of names is what makes a workspace slow at
 * exactly the moment it has become worth using.
 */
export async function listSheets(): Promise<SheetMeta[]> {
  const all = await run<StoredSheet[]>('readonly', store => store.getAll());
  return all
    .map(({ scene: _scene, ...meta }) => meta)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteSheet(id: string): Promise<void> {
  await run('readwrite', store => store.delete(id) as unknown as IDBRequest<undefined>);
  wrote();
}

/**
 * Every kept sheet with nothing on it.
 *
 * The owner opened his switcher and found thirty `Untitled` rows he had never drawn on
 * (`docs/owner-critique.md` §6). Not minting them is the real fix and it is done where they were
 * minted; this is for the thirty that already exist. It reads the full scenes, which `listSheets`
 * deliberately does not — but this runs once, on a press, and the alternative is asking him to
 * open thirty sheets to find out which are empty.
 *
 * The scratch sheet is never included. It is one row with a fixed id and it is empty by design.
 */
export async function emptySheets(): Promise<SheetRecord[]> {
  const all = await run<StoredSheet[]>('readonly', store => store.getAll());
  return all
    .map(fromStored)
    .filter(sheet => sheet.kind === 'kept' && sheet.scene.objects.length === 0)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Every scratch sheet, newest first. What the Scratch row lists under itself. */
export async function listScratch(): Promise<SheetMeta[]> {
  const all = await listSheets();
  return all.filter(sheet => sheet.kind === 'scratch');
}

/**
 * Forget scratch sheets nobody drew on.
 *
 * Pressing Scratch four times must leave one sheet, not four. A sheet is only written to disk at
 * its first mark, so most of them never existed to collect — this is for the ones that were
 * opened, written to, and then emptied again. `keep` is the sheet you are on, which is untouched
 * by definition at the moment it is made.
 */
export async function sweepUntouchedScratch(keep: string): Promise<number> {
  const all = await run<StoredSheet[]>('readonly', store => store.getAll());
  const doomed = all
    .map(fromStored)
    .filter(sheet => sheet.kind === 'scratch'
      && sheet.id !== keep
      && sheet.id !== SCRATCH_ID
      && sheet.scene.objects.length === 0);
  for (const sheet of doomed) await deleteSheet(sheet.id);
  return doomed.length;
}

/**
 * A debounced writer.
 *
 * Writing on every stroke point would put thousands of transactions through a single evening's
 * work; writing only on a timer loses the last edit when a tab closes. Both, then: a 250ms
 * debounce, plus an immediate flush on `visibilitychange`.
 */
export function createWriter(delay = 250) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: SheetRecord | null = null;
  let inFlight: Promise<void> = Promise.resolve();

  const flush = (): Promise<void> => {
    if (timer !== null) { clearTimeout(timer); timer = null; }
    const sheet = pending;
    pending = null;
    if (!sheet) return inFlight;
    inFlight = saveSheet(sheet).catch(() => {});
    return inFlight;
  };

  return {
    queue(sheet: SheetRecord) {
      pending = { ...sheet, scene: { objects: sheet.scene.objects } };
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => { void flush(); }, delay);
    },
    flush,
    get hasPending() { return pending !== null; },
  };
}


// ── The spine's stores ─────────────────────────────────────────────────────────────────────
// Generic because courses, chapters, tasks and log entries are all plain records keyed by id;
// the interesting logic is in curriculum.ts, and this file stays the seam a Supabase adapter
// slots into later.

type StoreName = (typeof STORES)[keyof typeof STORES];

const runOn = async <T,>(
  store: StoreName,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const request = work(tx.objectStore(store));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Strip anything the structured-clone algorithm cannot carry.
 *
 * Records reach this layer from a Vue `ref`, which hands back a reactive PROXY — and
 * `IDBObjectStore.put` refuses a proxy with `DataCloneError: [object Array] could not be
 * cloned`. The write then fails asynchronously, the in-memory list still shows the change, and
 * the loss only appears on the next reload. These records are all JSON-shaped, so a round trip
 * is both the cheapest unwrap and the strongest guarantee.
 */
const plain = <T,>(record: T): T => JSON.parse(JSON.stringify(record)) as T;

export const putRecord = async <T,>(store: StoreName, record: T): Promise<void> => {
  await runOn(store, 'readwrite', s => s.put(plain(record)) as IDBRequest<IDBValidKey>);
  wrote();
};

export const putMany = async <T,>(store: StoreName, records: readonly T[]): Promise<void> => {
  if (records.length === 0) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    for (const record of records) tx.objectStore(store).put(plain(record));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  wrote();
};

export const allRecords = <T,>(store: StoreName): Promise<T[]> =>
  runOn<T[]>(store, 'readonly', s => s.getAll());

export const getRecord = <T,>(store: StoreName, id: string): Promise<T | undefined> =>
  runOn<T | undefined>(store, 'readonly', s => s.get(id));

export const deleteRecord = async (store: StoreName, id: string): Promise<void> => {
  await runOn(store, 'readwrite', s => s.delete(id) as unknown as IDBRequest<undefined>);
  wrote();
};

/** Delete every record matching a predicate — used when a chapter or course is removed. */
export const deleteWhere = async <T extends { id: string }>(
  store: StoreName,
  matches: (record: T) => boolean,
): Promise<string[]> => {
  const all = await allRecords<T>(store);
  const doomed = all.filter(matches).map(record => record.id);
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    for (const id of doomed) tx.objectStore(store).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  wrote();
  return doomed;
};
